import React, { useEffect, useState, useCallback } from "react";
import OffersCard from "../OffersCard/OffersCard";
import LinearProgress from "@mui/material/LinearProgress";
import Alert from "@mui/material/Alert";
import "../OffersMain/offersmain.css";
import RequestSvc from "../../services/RequestSvc";



const OffersViewContainerGrid = ({ children, nichoCode = "Dinero" }) => {
    const [offers, setOffers] = useState([]);
    const [loading, setLoading] = useState(false);
    const [errorMsg, setErrorMsg] = useState("");
    const [infoMsg, setInfoMsg] = useState("");
    const [featureUsage, setFeatureUsage] = useState(null);
    const [isFeatureUsageLoading, setIsFeatureUsageLoading] = useState(false);
    // Pagination state
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [limit] = useState(10); // 2 rows of 5 ads

    // Fetch offers with pagination
    const fetchOffers = useCallback(async (nichoCode, page = 1, lim = 10) => {
        setLoading(true);
        setErrorMsg("");
        setInfoMsg("");
        try {
            const response = await fetch(
                `https://pulpoia-ops.com/backend/ads/getnicho?nichoCode=${nichoCode}&limit=${lim}&page=${page}`
            );
            const data = await response.json();
            if (data.error) {
                setErrorMsg("Ocurrió un error al obtener las ofertas.");
                setOffers([]);
                setTotalPages(1);
            } else if (Array.isArray(data.data) && data.data.length === 0) {
                setInfoMsg(data.message || "No se encontraron ofertas para este nicho.");
                setOffers([]);
                setTotalPages(1);
            } else {
                setOffers(data.data || []);
                setCurrentPage(data.info?.currentPage || 1);
                setTotalPages(data.info?.totalPages || 1);
            }
        } catch (err) {
            setErrorMsg("No se pudo conectar con el servidor.");
            setOffers([]);
            setTotalPages(1);
        }
        setLoading(false);
    }, []);

    useEffect(() => {
        fetchOffers(nichoCode, currentPage, limit);
    }, [nichoCode, currentPage, limit, fetchOffers]);

    useEffect(() => {
        let isMounted = true;

        const loadFeatureUsage = async () => {
            setIsFeatureUsageLoading(true);
            try {
                const requestSvc = new RequestSvc();
                const response = await requestSvc.get("https://pulpoia-ops.com/backend/ads/user-features");
                if (!isMounted) return;
                if (response?.error) {
                    console.warn('[OffersViewContainerGrid] Feature usage response error', response?.message);
                    setFeatureUsage(null);
                    return;
                }
                setFeatureUsage(Array.isArray(response?.data) ? response.data : null);
            } catch (err) {
                if (!isMounted) return;
                console.warn('[OffersViewContainerGrid] Feature usage fetch failed', err);
                setFeatureUsage(null);
            } finally {
                if (isMounted) setIsFeatureUsageLoading(false);
            }
        };

        loadFeatureUsage();

        return () => {
            isMounted = false;
        };
    }, []);

    // Pagination controls
    const Pagination = ({ currentPage, totalPages, onPageChange }) => {
        if (totalPages <= 1) return null;
        const pages = [];
        // Show up to 5 page numbers, with ellipsis if needed
        let start = Math.max(1, currentPage - 2);
        let end = Math.min(totalPages, currentPage + 2);
        if (currentPage <= 3) {
            end = Math.min(5, totalPages);
        } else if (currentPage >= totalPages - 2) {
            start = Math.max(1, totalPages - 4);
        }
        for (let i = start; i <= end; i++) {
            pages.push(i);
        }
        return (
            <div className="pagination">
                <button
                    className="paginationBtn"
                    onClick={() => onPageChange(1)}
                    disabled={currentPage === 1}
                >{'|<'}</button>
                <button
                    className="paginationBtn"
                    onClick={() => onPageChange(currentPage - 1)}
                    disabled={currentPage === 1}
                >Anterior</button>
                {start > 1 && <span className="paginationEllipsis">...</span>}
                {pages.map((p) => (
                    <button
                        key={p}
                        className={`paginationBtn${p === currentPage ? ' active' : ''}`}
                        onClick={() => onPageChange(p)}
                        disabled={p === currentPage}
                    >{p}</button>
                ))}
                {end < totalPages && <span className="paginationEllipsis">...</span>}
                <button
                    className="paginationBtn"
                    onClick={() => onPageChange(currentPage + 1)}
                    disabled={currentPage === totalPages}
                >Siguiente</button>
                <button
                    className="paginationBtn"
                    onClick={() => onPageChange(totalPages)}
                    disabled={currentPage === totalPages}
                >{'>|'}</button>
            </div>
        );
    };

    const handlePageChange = (page) => {
        if (page < 1 || page > totalPages || page === currentPage) return;
        setCurrentPage(page);
    };

    const ofeUsage = Array.isArray(featureUsage)
        ? featureUsage.find((entry) => typeof entry?.FK_FeaturePlanCode === 'string' && entry.FK_FeaturePlanCode.includes('-Ofe'))
        : null;
    const canShowPagination = !isFeatureUsageLoading && ofeUsage && Number(ofeUsage.TotalUsed) > 0;

    return (
        <div
            style={{
                background: "transparent",
                width: "100%",
                height: "100%",
                minHeight: "600px",
                padding: "24px",
                boxSizing: "border-box",
            }}
        >
            {children}
            {loading && <LinearProgress sx={{ margin: "24px 0" }} />}
            {errorMsg && (
                <Alert severity="error" sx={{ margin: "24px 0" }}>
                    {errorMsg}
                </Alert>
            )}
            {infoMsg && !loading && !errorMsg && (
                <Alert severity="info" sx={{ margin: "24px 0" }}>
                    {infoMsg}
                </Alert>
            )}
            <div
                className="grid"
                style={{ marginTop: "24px", alignItems: "start" }}
            >
                {!loading && !errorMsg && offers.map((oferta) => (
                    <OffersCard key={oferta.id + oferta.Imagen} offer={oferta} />
                ))}
            </div>
            {/* Pagination controls with separation */}
            {canShowPagination && (
                <div style={{ marginTop: '58px' }}>
                    <Pagination currentPage={currentPage} totalPages={totalPages} onPageChange={handlePageChange} />
                </div>
            )}
        </div>
    );
};

export default OffersViewContainerGrid;